export function toEntries<T>(a: T[]) {
  return a.map((value, index) => [index, value] as const);
}

export function decodeJwtPayload<T extends object = Record<string, unknown>>(
  token: string
): T | null {
  try {
    const encodedPayload = token.split(".")[1];
    if (!encodedPayload) return null;

    const base64 = encodedPayload.replace(/-/g, "+").replace(/_/g, "/");
    const paddedBase64 = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");

    return JSON.parse(atob(paddedBase64)) as T;
  } catch {
    return null;
  }
}

export function countNonEmptyValues(obj: Record<string, unknown>): number {
  let count = 0;

  for (const key in obj) {
    const value = obj[key];

    if (value instanceof Date) {
      count++;
    } else if (value !== null && value !== undefined) {
      if (typeof value === "string" && value.trim() !== "") {
        count++;
      } else if (typeof value === "number" && !isNaN(value)) {
        count++;
      } else if (typeof value === "boolean") {
        count++;
      } else if (Array.isArray(value) && value.length > 0) {
        count++;
      } else if (typeof value === "object" && Object.keys(value).length > 0) {
        count++;
      }
    }
  }

  return count;
}

export function getImageSizeFromFile(
  file?: File | null
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    if (!file) return { height: 0, width: 0 };

    const img = new Image();

    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };

    img.onerror = reject;

    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };

    reader.onerror = reject;

    reader.readAsDataURL(file);
  });
}

export const createUrlWithParams = (
  currentSearchParams: URLSearchParams,
  basePath: string,
  newParams: Record<string, string | undefined>
): string => {
  const params = new URLSearchParams(currentSearchParams);

  Object.entries(newParams).forEach(([key, value]) => {
    if (value === undefined) {
      params.delete(key);
    } else {
      params.set(key, value);
    }
  });

  return `${basePath}?${params.toString()}`;
};

export const linkifyText = (text: string): string => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  return text.replace(
    urlRegex,
    '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-brand-dark hover:underline">$1</a>'
  );
};

export function toCleanNumber(num: number | string): string {
  return parseFloat(String(num)).toString();
}

export function parseSalaryValue(value: string | number | null | undefined): number | null {
  if (value == null || value === "") return null;
  if (typeof value === "string") {
    const stripped = value.replace(/\s/g, "");
    return stripped === "" ? NaN : Number(stripped);
  }
  return value;
}

export function toNullableSalaryNumber(value: string | number | null | undefined): number | null {
  const parsed = parseSalaryValue(value);
  return typeof parsed === "number" && Number.isNaN(parsed) ? null : parsed;
}

export function getTextPreview(html: string, maxLength: number = 150): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const text = doc.body.textContent || "";

  return text.length > maxLength && maxLength > 0 ? text.slice(0, maxLength).trim() + "..." : text;
}

export function getTextPreviewFormatted(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const paragraphs = doc.querySelectorAll("p");
  paragraphs.forEach((p) => {
    p.insertAdjacentText("afterend", "\n\n");
  });

  const breaks = doc.querySelectorAll("br");
  breaks.forEach((br) => {
    br.insertAdjacentText("afterend", "\n");
  });

  const text = doc.body.textContent || "";

  return text.replace(/\n{3,}/g, "\n\n").trim();
}
